/**
 * Pure rules behind the cross-lingual search fallback. No runtime imports, so the self-check
 * (`npm run check:search-translation`) runs it with Node's type stripping.
 */

export type SearchTranslationTrigger = {
  enabled: boolean
  typedText: string | null | undefined
  hitCount: number
  firstHitRankingScore: number | undefined
  threshold: number
}

/**
 * Whether a typed search is weak enough to ask crate-core for a Query Translation: the flag is on,
 * the visitor typed something, and the search found nothing or its first hit scored below the
 * threshold. A hit without a score cannot be judged, so it never triggers a request.
 */
export function shouldRequestTranslation({
  enabled,
  typedText,
  hitCount,
  firstHitRankingScore,
  threshold,
}: SearchTranslationTrigger): boolean {
  if (!enabled || !typedText?.trim()) {
    return false
  }

  if (hitCount === 0) {
    return true
  }

  return firstHitRankingScore !== undefined && firstHitRankingScore < threshold
}

/**
 * Joins the visitor's typed text with a Content's curated search query into one Meilisearch `q`.
 * Searching with a Query Translation passes the English text as `typedText`; the curated part stays.
 */
export function buildTextQuery(typedText: string, curatedQuery?: string | null): string {
  return [typedText.trim(), curatedQuery?.trim() ?? ''].filter((part) => part.length > 0).join(' ')
}

/**
 * The English text to search with instead of the typed text, or null when there is none, when it
 * only differs in case, or when the visitor chose to search for the typed text instead.
 */
export function resolveTranslatedText(
  typedText: string | null | undefined,
  english: string | null | undefined,
  dismissed: boolean
): string | null {
  const typed = typedText?.trim()
  const translated = english?.trim()

  if (dismissed || !typed || !translated || translated.toLowerCase() === typed.toLowerCase()) {
    return null
  }

  return translated
}

export type QueryTranslation = {
  english: string | null
  language: string | null
}

/**
 * Reads crate-core's Query Translation body. A missing or blank English text, or a language that
 * is not a two-letter lowercase code (older crate-core sends none), becomes null.
 */
export function parseQueryTranslation(body: unknown): QueryTranslation {
  const { english, language } = (body ?? {}) as { english?: unknown; language?: unknown }

  return {
    english: typeof english === 'string' && english.trim() ? english : null,
    language: typeof language === 'string' && /^[a-z]{2}$/.test(language) ? language : null,
  }
}
